import React, { useState, useEffect } from 'react';
import Breadcrumbs from './components/Breadcrumbs';
import BasicDetailsTab from './components/BasicDetailsTab';
import SongsTab from './components/SongsTab';
import AlbumsTab from './components/AlbumsTab';
import DocumentsTab from './components/DocumentsTab';
import CasesNotesTab from './components/CasesNotesTab';
import ConfirmationModal from '../../components/shared/ConfirmationModal';
import en from '../../translations/en.json';
import { CREATE_ARTIST_URL, CHECK_ARTIST_NAME_URL, GET_ARTIST_SONGS_URL, DELETE_ARTIST_URL } from '../../api/artist';
import { apiClient } from '../../api/apiClient';
import { toast } from '../../components/shared/toast';
import './ArtistDetails.css';

function ArtistDetails({ artist, onBack, onSelectSong, onSelectAlbum, onSelectCase }) {
  const [activeTab, setActiveTab] = useState('Basic Details');
  const [isEditing, setIsEditing] = useState(false);

  // Edit form states
  const [editName, setEditName] = useState(artist.name || '');
  const [editGender, setEditGender] = useState(artist.gender === 'F' || artist.gender === 'Female' ? 'Female' : (artist.gender === 'O' || artist.gender === 'Other/Band' ? 'Other/Band' : 'Male'));
  const [editStatus, setEditStatus] = useState(
    artist.status === 'Active' || artist.status === true || artist.status === 'true'
  );
  const [editTypes, setEditTypes] = useState({
    music: artist.types?.includes('music') || false,
    lyrics: artist.types?.includes('lyrics') || false,
    singer: artist.types?.includes('singer') || false,
    band: artist.types?.includes('band') || false,
    other: artist.types?.includes('other') || false
  });
  const [editImage, setEditImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(artist.avatar || '');
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Inactivate / Delete Modals State
  const [showPrimaryInactivateModal, setShowPrimaryInactivateModal] = useState(false);
  const [showPrimaryDeleteModal, setShowPrimaryDeleteModal] = useState(false);
  const [showSongsDependencyModal, setShowSongsDependencyModal] = useState(false);
  const [dependencyModalMode, setDependencyModalMode] = useState('inactivate'); // 'inactivate' | 'delete'
  const [dependentSongs, setDependentSongs] = useState([]);

  // Sync state when artist changes
  useEffect(() => {
    setEditName(artist.name || '');
    setEditGender(artist.gender === 'F' || artist.gender === 'Female' ? 'Female' : (artist.gender === 'O' || artist.gender === 'Other/Band' ? 'Other/Band' : 'Male'));
    setEditStatus(artist.status === 'Active' || artist.status === true || artist.status === 'true');
    setEditTypes({
      music: artist.types?.includes('music') || false,
      lyrics: artist.types?.includes('lyrics') || false,
      singer: artist.types?.includes('singer') || false,
      band: artist.types?.includes('band') || false,
      other: artist.types?.includes('other') || false
    });
    setEditImage(null);
    setImagePreview(artist.avatar || '');
  }, [artist]);

  const t = (key) => {
    return key.split('.').reduce((obj, i) => (obj ? obj[i] : null), en) || key;
  };

  const capitalizeName = (str) => {
    if (!str) return '';
    return str
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const tabs = [
    { id: 'Basic Details', label: t('artistDetails.tabBasicDetails') },
    { id: 'Songs', label: t('artistDetails.tabSongs') },
    { id: 'Albums', label: t('artistDetails.tabAlbums') },
    { id: 'Documents', label: t('artistDetails.tabDocuments') },
    { id: 'Cases and Notes', label: t('artistDetails.tabCasesNotes') }
  ];

  const proceedSave = async () => {
    try {
      const formData = new FormData();
      formData.append('name', editName);
      formData.append('gender', editGender === 'Female' ? 'F' : (editGender === 'Other/Band' ? 'O' : 'M'));
      formData.append('status', String(editStatus));
      
      const selectedTypes = Object.keys(editTypes).filter(k => editTypes[k]);
      selectedTypes.forEach(t => formData.append('types', t));

      if (editImage) {
        formData.append('image', editImage);
      }

      const updatedArtist = await apiClient(`${CREATE_ARTIST_URL}/${artist.id}`, {
        method: 'PUT',
        body: formData,
      });

      toast.success('Artist updated successfully!');
      
      // Update properties on the artist prop directly
      artist.name = updatedArtist.name;
      artist.gender = updatedArtist.gender === 'F' ? 'Female' : (updatedArtist.gender === 'O' ? 'Other/Band' : 'Male');
      artist.status = updatedArtist.status ? 'Active' : 'Inactive';
      artist.types = updatedArtist.types;
      if (updatedArtist.imageUrl) {
        artist.avatar = updatedArtist.imageUrl;
      }

      setIsEditing(false);
    } catch (err) {
      console.warn('Backend update failed, falling back to mock:', err);
      
      const selectedTypes = Object.keys(editTypes).filter(k => editTypes[k]);
      artist.name = capitalizeName(editName);
      artist.gender = editGender;
      artist.status = editStatus ? 'Active' : 'Inactive';
      artist.types = selectedTypes;
      if (editImage) {
        artist.avatar = imagePreview;
      }

      toast.success('Artist updated successfully!');
      setIsEditing(false);
    }
  };

  const handleSave = async () => {
    // Validations
    if (!editName.trim()) {
      toast.warning('Artist Name is required.');
      return;
    }
    const typeSelected = Object.values(editTypes).some(val => val === true);
    if (!typeSelected) {
      toast.warning('Please select at least one Artist Type.');
      return;
    }
    if (!editGender) {
      toast.warning('Gender is required.');
      return;
    }

    try {
      const checkResult = await apiClient(CHECK_ARTIST_NAME_URL, {
        method: 'POST',
        body: { name: editName, id: artist.id }
      });
      if (checkResult.exists) {
        setShowConfirmModal(true);
        return;
      }
    } catch (err) {
      console.warn('Backend name duplicate check failed:', err);
    }
    await proceedSave();
  };

  const handleConfirmDuplicate = async () => {
    setShowConfirmModal(false);
    await proceedSave();
  };

  const handleCancelDuplicate = () => {
    setShowConfirmModal(false);
  };

  const handleCancel = () => {
    setEditName(artist.name || '');
    setEditGender(artist.gender === 'F' || artist.gender === 'Female' ? 'Female' : (artist.gender === 'O' || artist.gender === 'Other/Band' ? 'Other/Band' : 'Male'));
    setEditStatus(artist.status === 'Active' || artist.status === true || artist.status === 'true');
    setEditTypes({
      music: artist.types?.includes('music') || false,
      lyrics: artist.types?.includes('lyrics') || false,
      singer: artist.types?.includes('singer') || false,
      band: artist.types?.includes('band') || false,
      other: artist.types?.includes('other') || false
    });
    setEditImage(null);
    setImagePreview(artist.avatar || '');
    setIsEditing(false);
  };

  // Handle Inactivate Flow
  const handleInactivateClick = () => {
    setShowPrimaryInactivateModal(true);
  };

  const handleConfirmPrimaryInactivate = async () => {
    setShowPrimaryInactivateModal(false);
    try {
      const songsRes = await apiClient(`${GET_ARTIST_SONGS_URL(artist.id)}?export=true`);
      const activeSongs = songsRes?.songs || [];

      if (activeSongs.length > 0) {
        setDependentSongs(activeSongs);
        setDependencyModalMode('inactivate');
        setShowSongsDependencyModal(true);
        return;
      }

      const formData = new FormData();
      formData.append('name', artist.name);
      formData.append('gender', artist.gender === 'Female' || artist.gender === 'F' ? 'F' : (artist.gender === 'Other/Band' || artist.gender === 'O' ? 'O' : 'M'));
      formData.append('status', 'false');
      
      const selectedTypes = artist.types || [];
      selectedTypes.forEach(t => formData.append('types', t));
      
      const updated = await apiClient(`${CREATE_ARTIST_URL}/${artist.id}`, {
        method: 'PUT',
        body: formData
      });
      
      artist.status = 'Inactive';
      setEditStatus(false);
      toast.success('Artist status updated to Inactive!');
    } catch (err) {
      console.error('Error inactivating artist:', err);
      toast.error(err?.message || 'Cannot inactivate artist with active linked songs.');
    }
  };

  // Handle Reactivate Flow
  const handleReactivateClick = async () => {
    try {
      const formData = new FormData();
      formData.append('name', artist.name);
      formData.append('gender', artist.gender === 'Female' || artist.gender === 'F' ? 'F' : (artist.gender === 'Other/Band' || artist.gender === 'O' ? 'O' : 'M'));
      formData.append('status', 'true');
      
      const selectedTypes = artist.types || [];
      selectedTypes.forEach(t => formData.append('types', t));
      
      const updated = await apiClient(`${CREATE_ARTIST_URL}/${artist.id}`, {
        method: 'PUT',
        body: formData
      });
      
      artist.status = 'Active';
      setEditStatus(true);
      toast.success('Artist status updated to Active!');
    } catch (err) {
      console.error('Error reactivating artist:', err);
      toast.error('Failed to reactivate artist.');
    }
  };

  // Handle Delete Flow
  const handleDeleteClick = () => {
    setShowPrimaryDeleteModal(true);
  };

  const handleConfirmPrimaryDelete = async () => {
    setShowPrimaryDeleteModal(false);
    try {
      const songsRes = await apiClient(`${GET_ARTIST_SONGS_URL(artist.id)}?export=true`);
      const activeSongs = songsRes?.songs || [];

      if (activeSongs.length > 0) {
        setDependentSongs(activeSongs);
        setDependencyModalMode('delete');
        setShowSongsDependencyModal(true);
        return;
      }

      await apiClient(DELETE_ARTIST_URL(artist.id), {
        method: 'DELETE'
      });
      
      toast.success('Artist deleted successfully!');
      if (onBack) onBack();
    } catch (err) {
      console.error('Error deleting artist:', err);
      toast.error(err?.message || 'Failed to delete artist.');
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'Basic Details':
        return (
          <BasicDetailsTab 
            artist={artist} 
            isEditing={isEditing}
            editName={editName}
            setEditName={setEditName}
            editGender={editGender}
            setEditGender={setEditGender}
            editStatus={editStatus}
            setEditStatus={setEditStatus}
            editTypes={editTypes}
            setEditTypes={setEditTypes}
          />
        );
      case 'Songs':
        return <SongsTab artist={artist} onSelectSong={onSelectSong} />;
      case 'Albums':
        return <AlbumsTab artist={artist} onSelectAlbum={onSelectAlbum} />;
      case 'Documents':
        return <DocumentsTab artist={artist} />;
      case 'Cases and Notes':
        return <CasesNotesTab artist={artist} onSelectCase={onSelectCase} />;
      default:
        return null;
    }
  };

  const artistName = artist.name || 'Artist Name';
  const avatarUrl = imagePreview || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&q=80&w=120&h=120';
  const isArtistActive = artist.status === 'Active' || artist.status === true || artist.status === 'true';

  return (
    <div className="artist-details-page">
      {/* Breadcrumbs Section */}
      <Breadcrumbs artistName={artistName} onBack={onBack} />

      {/* Profile Header / Banner Section */}
      <div className="artist-details-banner">
        <div className="banner-left-content">
          <div className="artist-status-indicator" style={{
            backgroundColor: isArtistActive ? '#137333' : '#c5221f',
            color: '#ffffff',
            borderColor: 'transparent'
          }}>
            <span className="indicator-dot" style={{ backgroundColor: '#ffffff' }}></span>
            <span className="indicator-text" style={{ color: '#ffffff' }}>
              {isArtistActive ? 'Active' : 'Inactive'}
            </span>
          </div>
          
          <div className="artist-title-with-badge">
            <h1 className="artist-main-title">{artistName}</h1>
            {/* Verified blue checkmark badge icon */}
            <div className="verified-check-badge" title="Verified Artist">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="#0b66e3" stroke="#ffffff" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="9 12 11 14 15 10" />
              </svg>
            </div>
          </div>
          
          <p className="artist-main-subtitle">Artist Profile</p>
        </div>

        {/* Artwork card showing artist photo or label */}
        <div className={`artist-artwork-box ${isEditing ? 'editable' : ''}`} style={{ position: 'relative' }}>
          <img src={avatarUrl} alt={artistName} className="artwork-image" />
          {isEditing ? (
            <>
              <div 
                className="artwork-edit-overlay" 
                onClick={() => document.getElementById('edit-artwork-input').click()}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  cursor: 'pointer',
                  borderRadius: '12px',
                  opacity: 1,
                  transition: 'background-color 0.2s',
                  gap: '8px'
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                <span style={{ fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Upload Image</span>
              </div>
              <input
                id="edit-artwork-input"
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    setEditImage(file);
                    setImagePreview(URL.createObjectURL(file));
                  }
                }}
              />
            </>
          ) : (
            <div className="artwork-label-card">
              <span className="artwork-label-bold">Artist</span>
              <span className="artwork-label-small">Artwork</span>
            </div>
          )}
        </div>
      </div>

      {/* Tab Row Selectors */}
      <div className="artist-details-tabs-row">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`details-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Content panel body card */}
      <div className="artist-details-content-card">
        <div className="tab-panel-body">
          {renderTabContent()}
        </div>

        {/* Conditionally render bottom buttons for Basic Details tab only */}
        {activeTab === 'Basic Details' && (
          <div className="artist-details-footer">
            {isEditing ? (
              <>
                <button 
                  type="button" 
                  className="form-btn-primary"
                  onClick={handleSave}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                    <polyline points="17 21 17 13 7 13 7 21" />
                    <polyline points="7 3 7 8 15 8" />
                  </svg>
                  <span>Save</span>
                </button>
                <button 
                  type="button" 
                  className="form-btn-outlined"
                  onClick={handleCancel}
                  style={{ cursor: 'pointer' }}
                >
                  Cancel
                </button>
              </>
            ) : isArtistActive ? (
              <>
                <button 
                  type="button" 
                  className="footer-outlined-btn edit-btn"
                  onClick={() => setIsEditing(true)}
                  style={{ cursor: 'pointer' }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M12 20h9" />
                    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
                  </svg>
                  <span>{t('artistDetails.editBtn')}</span>
                </button>

                <button 
                  type="button" 
                  className="footer-outlined-btn inactive-btn"
                  onClick={handleInactivateClick}
                  style={{ cursor: 'pointer' }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <span>Mark Inactive</span>
                </button>
              </>
            ) : (
              <>
                <button 
                  type="button" 
                  className="footer-outlined-btn edit-btn"
                  onClick={() => setIsEditing(true)}
                  style={{ cursor: 'pointer' }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M12 20h9" />
                    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
                  </svg>
                  <span>{t('artistDetails.editBtn')}</span>
                </button>

                <button 
                  type="button" 
                  className="footer-outlined-btn inactive-btn"
                  onClick={handleReactivateClick}
                  style={{ cursor: 'pointer', borderColor: '#16a34a', color: '#16a34a' }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 8 12 12 14 14" />
                  </svg>
                  <span>Reactivate Artist</span>
                </button>

                <button 
                  type="button" 
                  className="footer-deactivate-btn"
                  onClick={handleDeleteClick}
                  style={{ cursor: 'pointer', backgroundColor: '#ef4444', color: '#ffffff', padding: '8px 16px', borderRadius: '8px', display: 'inline-flex', alignItems: 'center', gap: '6px', fontWeight: '600' }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                  <span>Delete Artist</span>
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Duplicate Name Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        title={t('artists.confirmDuplicateTitle')}
        message={t('artists.confirmDuplicateMessage')}
        confirmLabel={t('common.confirm')}
        cancelLabel={t('common.cancel')}
        onConfirm={handleConfirmDuplicate}
        onCancel={handleCancelDuplicate}
      />

      {/* Primary Inactivate Confirmation Modal */}
      <ConfirmationModal
        isOpen={showPrimaryInactivateModal}
        type="warning"
        title="Inactivate Artist"
        message={`Are you sure you want to mark the artist "${artistName}" as Inactive?`}
        confirmLabel={t('common.confirm') || 'Confirm'}
        cancelLabel={t('common.cancel') || 'Cancel'}
        onConfirm={handleConfirmPrimaryInactivate}
        onCancel={() => setShowPrimaryInactivateModal(false)}
      />

      {/* Primary Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showPrimaryDeleteModal}
        type="danger"
        title="Delete Artist"
        message={`Are you sure you want to permanently delete the artist "${artistName}"?`}
        confirmLabel={t('common.confirm') || 'Confirm'}
        cancelLabel={t('common.cancel') || 'Cancel'}
        onConfirm={handleConfirmPrimaryDelete}
        onCancel={() => setShowPrimaryDeleteModal(false)}
      />

      {/* Active Songs Dependency Warning Modal */}
      {showSongsDependencyModal && (
        <div className="modal-backdrop-overlay" style={{ zIndex: 10000 }}>
          <div className="modal-card-container" style={{ width: '520px', padding: '24px' }} role="dialog" aria-modal="true">
            <button 
              className="modal-close-icon-btn" 
              onClick={() => {
                setShowSongsDependencyModal(false);
                setDependentSongs([]);
              }}
              aria-label="Close dialog"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            <div className="modal-content-wrapper" style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
              <div className="modal-icon-container danger" style={{ backgroundColor: '#fee2e2', color: '#ef4444', borderRadius: '50%', width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </div>
              <div className="modal-text-content">
                <h3 className="modal-title-heading" style={{ fontSize: '1.2rem', fontWeight: '700', margin: 0 }}>
                  {dependencyModalMode === 'inactivate' ? 'Cannot Inactivate Artist' : 'Cannot Delete Artist'}
                </h3>
                <p className="modal-description-text" style={{ fontSize: '0.9rem', color: '#ef4444', fontWeight: '600', marginTop: '6px' }}>
                  {dependencyModalMode === 'inactivate' 
                    ? 'Cannot inactivate this artist without remove all songs from this artist until.' 
                    : 'Cannot delete this artist without remove all songs from this artist until.'}
                </p>
              </div>
            </div>

            <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '8px', fontWeight: '500' }}>
              The following active songs are currently linked to this artist:
            </div>

            <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px', marginBottom: '20px', background: '#f8fafc' }}>
              {dependentSongs.map((song) => (
                <div key={song.id} style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '8px', marginBottom: '8px' }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-main)' }}>{song.name}</div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>ISRC: {song.isrcCode || '—'} | Artist: {song.artist || artistName}</div>
                </div>
              ))}
            </div>

            <div className="modal-footer-actions" style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button 
                type="button" 
                className="modal-btn modal-btn-cancel" 
                onClick={() => {
                  setShowSongsDependencyModal(false);
                  setDependentSongs([]);
                }}
                style={{ backgroundColor: '#0b66e3', color: '#ffffff', padding: '8px 20px', borderRadius: '6px', fontWeight: '600' }}
              >
                OK, Got It
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ArtistDetails;
